import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { leadAPI, taskAPI, CRMStats, Lead, CRMTask, LeadActivity } from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

const CRMDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<CRMStats | null>(null);
  const [myTasks, setMyTasks] = useState<CRMTask[]>([]);
  const [upcomingFollowUps, setUpcomingFollowUps] = useState<Lead[]>([]);
  const [recentActivity, setRecentActivity] = useState<LeadActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, tasksRes, followUpsRes, activityRes] = await Promise.all([
        leadAPI.getStats(),
        taskAPI.getMyTasks(),
        leadAPI.getUpcomingFollowUps(),
        leadAPI.getRecentActivity(),
      ]);

      setStats(statsRes.data);
      setMyTasks(tasksRes.data);
      setUpcomingFollowUps(followUpsRes.data);
      setRecentActivity(activityRes.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId: number) => {
    try {
      await taskAPI.complete(taskId);
      setMyTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'CALL': return '📞';
      case 'EMAIL': return '📧';
      case 'VISIT': return '🚗';
      case 'MEETING': return '👥';
      case 'NOTE': return '📝';
      case 'SAMPLE_SENT': return '📦';
      case 'QUOTE_SENT': return '💰';
      default: return '📌';
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="crm-page">
        <CRMSubNav />
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="crm-page">
      <CRMSubNav />

      <div className="crm-header">
        <h1>CRM Dashboard</h1>
        <div className="crm-header-actions">
          <Link to="/crm/leads/new" className="btn btn-primary">
            + Add Lead
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="crm-stats">
          <div className="stat-card">
            <h3>Total Leads</h3>
            <div className="stat-value">{stats.total_leads}</div>
          </div>
          <div className="stat-card stat-new">
            <h3>New This Month</h3>
            <div className="stat-value">{stats.new_this_month}</div>
          </div>
          <div className="stat-card stat-pipeline">
            <h3>In Pipeline</h3>
            <div className="stat-value">{stats.in_pipeline}</div>
          </div>
          <div className="stat-card stat-converted">
            <h3>Converted This Month</h3>
            <div className="stat-value">{stats.converted_this_month}</div>
          </div>
          {stats.overdue_follow_ups > 0 && (
            <div className="stat-card stat-overdue">
              <h3>Overdue Follow-ups</h3>
              <div className="stat-value">{stats.overdue_follow_ups}</div>
            </div>
          )}
          {/* Work nobody owns - a call task whose staff member could not be resolved.
              Shown only when there is some, so it reads as an exception, not furniture. */}
          {!!stats.unassigned_tasks && stats.unassigned_tasks > 0 && (
            <Link to="/crm/tasks" className="stat-card stat-overdue">
              <h3>Unassigned Tasks</h3>
              <div className="stat-value">{stats.unassigned_tasks}</div>
            </Link>
          )}
        </div>
      )}

      <div className="crm-grid">
        {/* My Tasks */}
        <div className="crm-card">
          <div className="crm-card-header">
            <h2>My Tasks</h2>
            <Link to="/crm/tasks" className="btn btn-sm btn-outline">View All</Link>
          </div>
          <div className="crm-card-body">
            {myTasks.length === 0 ? (
              <div className="empty-state">
                <p>No pending tasks</p>
              </div>
            ) : (
              <ul className="task-list">
                {myTasks.map(task => (
                  <li key={task.id} className={`task-item ${task.is_overdue ? 'overdue' : ''}`}>
                    <input
                      type="checkbox"
                      className="task-checkbox"
                      onChange={() => handleCompleteTask(task.id)}
                    />
                    <div className="task-content">
                      <div className="task-title">{task.title}</div>
                      <div className="task-meta">
                        {task.lead_name && (
                          <span onClick={() => navigate(`/crm/leads/${task.lead_id}`)} style={{ cursor: 'pointer', color: '#3b82f6' }}>
                            {task.lead_name}
                          </span>
                        )}
                        {task.customer_name && <span>{task.customer_name}</span>}
                      </div>
                    </div>
                    <div className={`task-due ${task.is_overdue ? 'overdue' : ''}`}>
                      {formatDate(task.due_date)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Upcoming Follow-ups */}
        <div className="crm-card">
          <div className="crm-card-header">
            <h2>Upcoming Follow-ups</h2>
            <Link to="/crm/leads?filter=follow_up" className="btn btn-sm btn-outline">View All</Link>
          </div>
          <div className="crm-card-body">
            {upcomingFollowUps.length === 0 ? (
              <div className="empty-state">
                <p>No upcoming follow-ups</p>
              </div>
            ) : (
              <ul className="followup-list">
                {upcomingFollowUps.slice(0, 8).map(lead => (
                  <li
                    key={lead.id}
                    className="followup-item"
                    onClick={() => navigate(`/crm/leads/${lead.id}`)}
                  >
                    <div>
                      <div className="followup-business">{lead.business_name}</div>
                      <div className="followup-contact">{lead.contact_name}</div>
                    </div>
                    <div className={`followup-date ${lead.is_follow_up_overdue ? 'overdue' : ''}`}>
                      {lead.next_follow_up ? formatDate(lead.next_follow_up) : 'No date'}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="crm-card" style={{ gridColumn: '1 / -1' }}>
          <div className="crm-card-header">
            <h2>Recent Activity</h2>
          </div>
          <div className="crm-card-body">
            {recentActivity.length === 0 ? (
              <div className="empty-state">
                <p>No recent activity</p>
              </div>
            ) : (
              <ul className="activity-feed">
                {recentActivity.slice(0, 10).map(activity => (
                  <li key={activity.id} className="activity-item">
                    <div className={`activity-icon ${activity.activity_type.toLowerCase()}`}>
                      {getActivityIcon(activity.activity_type)}
                    </div>
                    <div className="activity-content">
                      <div className="activity-subject">{activity.subject}</div>
                      <div className="activity-lead">
                        <span
                          onClick={() => navigate(`/crm/leads/${activity.lead_id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          {activity.lead_name}
                        </span>
                      </div>
                      <div className="activity-meta">
                        {activity.created_by} · {formatTimeAgo(activity.created_at)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMDashboard;
